import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, HostListener, Inject, Renderer2, ViewEncapsulation } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { animationOptionsMap } from 'app/shared/helpers';
import { slugify } from 'app/shared/helpers/utilities.helper';
import { environment } from 'environments/environment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
	selector: 'clearcals-web-story',
	templateUrl: './web-story-detail.component.html',
	styleUrls: ['./web-story-detail.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class WebStoryDetailComponent {
	data: any;
	nextStoryUrl: string;
	nextStoryTitle: string;
	animationOptionsMap = animationOptionsMap;
	private unsubscribe$ = new Subject<void>();

	private ampScripts: HTMLElement[] = [];
	private ampStyles: HTMLElement[] = [];

	constructor(
		private http: HttpClient,
		private route: ActivatedRoute,
		@Inject(DOCUMENT) private _document: Document,
		private meta: Meta,
		private titleService: Title,
		private renderer: Renderer2,
		private router: Router,
	) {
		this.addAmpScripts();
		this.addAmpStyles();
		this.addAnalytics();
	}

	ngOnInit() {
		const id = this.route.snapshot.paramMap.get('id');

		// adding 'amp' attribute on the root html tag
		this.renderer.setAttribute(this._document.documentElement, 'amp', '');

		const url = `${environment.appBaseUrl}/webstories/${id}/amp`;
		this.addCanonicalLink(url);

		this.http
			.get('assets/web-stories-data/web-stories.json')
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				next: (data: any) => {
					const indexOfCurrentStory = data['web-story'].findIndex(
						(story: any) => slugify(story.title) === id,
					);
					const indexOfNextStory =
						indexOfCurrentStory + 1 >= data['web-story'].length ? 0 : indexOfCurrentStory + 1;

					this.data = data['web-story'][indexOfCurrentStory];
					const nextStory = data['web-story'][indexOfNextStory];
					this.nextStoryUrl = `/webstories/${slugify(nextStory.title)}/amp`;
					this.nextStoryTitle = nextStory.title;

					this.updateMetaInfo(this.data.title, this.data.description, url, this.data.image);
				},
				error: (error) => {
					console.error('Error:', error);
				},
			});
	}

	addCanonicalLink(url: string) {
		const link = this.renderer.createElement('link');
		this.renderer.setAttribute(link, 'rel', 'canonical');
		this.renderer.setAttribute(link, 'href', url);
		this.renderer.appendChild(this._document.head, link);
	}

	updateMetaInfo(title: string, description: string, url: string, image: string): void {
		this.titleService.setTitle(title);

		this.meta.updateTag({ name: 'description', content: description });

		this.meta.updateTag({ property: 'og:url', content: url });
		this.meta.updateTag({ property: 'og:title', content: title });
		this.meta.updateTag({ property: 'og:image', content: image });
		this.meta.updateTag({
			property: 'og:description',
			content: description,
		});

		this.meta.updateTag({ name: 'twitter:url', content: url });
		this.meta.updateTag({ name: 'twitter:title', content: title });
		this.meta.updateTag({ name: 'twitter:image', content: image });
		this.meta.updateTag({
			name: 'twitter:description',
			content: description,
		});
	}

	addAmpScripts() {
		this.addScript('https://cdn.ampproject.org/v0.js');
		this.addScript('https://cdn.ampproject.org/v0/amp-story-1.0.js', 'amp-story');
		this.addScript('https://cdn.ampproject.org/v0/amp-video-0.1.js', 'amp-video');
		this.addScript('https://cdn.ampproject.org/v0/amp-analytics-0.1.js', 'amp-analytics');
	}

	addScript(src: string, customElement?: string) {
		const script = this.renderer.createElement('script');
		script.src = src;
		script.async = true;
		if (customElement) {
			script.setAttribute('custom-element', customElement);
		}
		this.renderer.appendChild(this._document.head, script);

		this.ampScripts.push(script);
	}

	addAmpStyles() {
		const style = this.renderer.createElement('style');
		style.setAttribute('amp-boilerplate', '');
		style.textContent = `body{-webkit-animation:-amp-start 8s steps(1,end) 0s 1 normal both;-moz-animation:-amp-start 8s steps(1,end) 0s 1 normal both;-ms-animation:-amp-start 8s steps(1,end) 0s 1 normal both;animation:-amp-start 8s steps(1,end) 0s 1 normal both}@-webkit-keyframes -amp-start{from{visibility:hidden}to{visibility:visible}}@-moz-keyframes -amp-start{from{visibility:hidden}to{visibility:visible}}@-ms-keyframes -amp-start{from{visibility:hidden}to{visibility:visible}}@-o-keyframes -amp-start{from{visibility:hidden}to{visibility:visible}}@keyframes -amp-start{from{visibility:hidden}to{visibility:visible}}`;

		const noscript = this.renderer.createElement('noscript');
		const noscriptStyle = this.renderer.createElement('style');
		noscriptStyle.setAttribute('amp-boilerplate', '');
		noscriptStyle.textContent = `body{-webkit-animation:none;-moz-animation:none;-ms-animation:none;animation:none}`;
		this.renderer.appendChild(noscript, noscriptStyle);

		this.renderer.appendChild(this._document.head, style);
		this.renderer.appendChild(this._document.head, noscript);

		this.ampStyles.push(style, noscript);
	}

	addAnalytics() {
		const ampAnalytics = this.renderer.createElement('amp-analytics');
		ampAnalytics.setAttribute('type', 'gtag');
		ampAnalytics.setAttribute('data-credentials', 'include');

		const script = this.renderer.createElement('script');
		script.type = 'application/json';
		script.text = `{
			"vars" : {
				"gtag_id": "G-MXYC5FH7Q6",
				"config" : {
					"G-MXYC5FH7Q6": { "groups": "default" }
				}
			}
		}`;

		this.renderer.appendChild(ampAnalytics, script);
		this.renderer.insertBefore(this._document.body, ampAnalytics, this._document.body.firstChild);

		this.ampScripts.push(ampAnalytics);
	}

	ngOnDestroy() {
		// Remove the scripts and styles from the DOM
		this.ampScripts.forEach((script) => this.renderer.removeChild(this._document.head, script));
		this.ampStyles.forEach((style) => this.renderer.removeChild(this._document.head, style));

		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	@HostListener('window:popstate', ['$event'])
	onPopState(event) {
		location.reload();
	}
}
